<template>
  <div>
    <b-card>
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h4 class="mb-0">{{ $t("Periodos de Planilla") }}</h4>
        <b-button
          variant="primary"
          :disabled="!!openPeriod"
          @click="openOpenModal"
        >
          <feather-icon icon="PlusIcon" class="mr-50" />
          {{ $t("Abrir Periodo") }}
        </b-button>
      </div>

      <b-alert v-if="openPeriod" show variant="info" class="p-1 mb-2">
        <feather-icon icon="InfoIcon" class="mr-50" />
        {{ $t("Periodo activo") }}: <strong>{{ monthName(openPeriod.month) }} {{ openPeriod.year }}</strong>
        ({{ openPeriod.start_date }} - {{ openPeriod.end_date }}).
        {{ $t("Debe cerrarlo para poder abrir un nuevo periodo.") }}
      </b-alert>
      <b-alert v-else show variant="warning" class="p-1 mb-2">
        <feather-icon icon="AlertTriangleIcon" class="mr-50" />
        {{ $t("No hay ningún periodo abierto. No se podrán calcular planillas hasta abrir uno.") }}
      </b-alert>

      <b-table
        :items="periods"
        :fields="fields"
        :busy="loading"
        show-empty
        responsive
        striped
      >
        <template #cell(month)="{ item }">
          {{ monthName(item.month) }}
        </template>

        <template #cell(status)="{ item }">
          <b-badge :variant="item.status === 1 ? 'success' : 'primary'">
            {{ item.status === 1 ? $t("Abierto") : $t("Cerrado") }}
          </b-badge>
        </template>

        <template #cell(total_aprobadas)="{ item }">
          {{ item.total_aprobadas || 0 }}
        </template>

        <template #cell(total_pendientes)="{ item }">
          {{ item.total_pendientes || 0 }}
        </template>

        <template #cell(total_anuladas)="{ item }">
          {{ item.total_anuladas || 0 }}
        </template>

        <template #cell(monto_total_pagado)="{ item }">
          S/. {{ formatCurrency(item.monto_total_pagado) }}
        </template>

        <template #cell(actions)="{ item }">
          <div class="d-flex align-items-center">
            <b-button
              size="sm"
              :variant="item.status === 1 ? 'outline-danger' : 'outline-secondary'"
              @click="openCloseSummary(item)"
              class="text-nowrap mr-50"
            >
              {{ item.status === 1 ? $t("Ver resumen / Cerrar") : $t("Ver resumen") }}
            </b-button>

            <b-dropdown
              variant="link"
              no-caret
              right
              toggle-class="p-0 text-muted"
            >
              <template #button-content>
                <b-button
                  size="sm"
                  variant="flat-secondary"
                  class="btn-icon rounded-circle"
                  v-b-tooltip.hover.bottom="$t('Más opciones')"
                >
                  <feather-icon icon="MoreVerticalIcon" size="16" />
                </b-button>
              </template>

              <b-dropdown-item @click="downloadReportePagos(item)">
                <feather-icon icon="FileTextIcon" class="mr-50 text-success" />
                <span>{{ $t("Reporte de Pagos") }}</span>
              </b-dropdown-item>

              <b-dropdown-item @click="openExportAfpnetModal(item)">
                <feather-icon icon="DownloadIcon" class="mr-50 text-primary" />
                <span>{{ $t("Exportar AFPnet") }}</span>
              </b-dropdown-item>

              <b-dropdown-item @click="openPagoAfpModal(item)">
                <feather-icon icon="DollarSignIcon" class="mr-50 text-warning" />
                <span>{{ $t("Pago AFP") }}</span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </b-table>
    </b-card>

    <b-modal
      v-model="showOpenModal"
      :title="$t('Abrir nuevo periodo')"
      centered
      @ok.prevent="confirmOpenPeriod"
      :ok-title="$t('Abrir Periodo')"
      :cancel-title="$t('Cancelar')"
      :ok-disabled="isBusy"
    >
      <b-form-group :label="$t('Año')">
        <b-form-input v-model.number="openForm.year" type="number" min="2000" />
      </b-form-group>
      <b-form-group :label="$t('Mes')">
        <v-select
          v-model="openForm.month"
          :options="months"
          :reduce="(m) => m.id"
          label="label"
          :clearable="false"
        />
      </b-form-group>
    </b-modal>

    <close-period-summary-modal
      v-if="selectedPeriod"
      v-model="showCloseSummaryModal"
      :period="selectedPeriod"
      @closed="onPeriodClosed"
    />

    <export-afpnet-modal
      v-if="selectedPeriod"
      v-model="showExportAfpnetModal"
      :period="selectedPeriod"
      :months="months"
    />

    <pago-afp-modal
      v-if="selectedPeriod"
      v-model="showPagoAfpModal"
      :period="selectedPeriod"
      :months="months"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import PayrollPeriodService from "@/views/rrhh/views/payroll/services/payroll-period.service";
import ClosePeriodSummaryModal from "./ClosePeriodSummaryModal.vue";
import ExportAfpnetModal from "./ExportAfpnetModal.vue";
import PagoAfpModal from "./PagoAfpModal.vue";

export default {
  name: "PayrollPeriods",
  components: {
    ClosePeriodSummaryModal,
    ExportAfpnetModal,
    PagoAfpModal,
  },
  data() {
    return {
      periods: [],
      loading: false,
      isBusy: false,
      showOpenModal: false,
      showCloseSummaryModal: false,
      showExportAfpnetModal: false,
      showPagoAfpModal: false,
      selectedPeriod: null,
      openForm: {
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
      },
      months: [
        { id: 1, label: "Enero" },
        { id: 2, label: "Febrero" },
        { id: 3, label: "Marzo" },
        { id: 4, label: "Abril" },
        { id: 5, label: "Mayo" },
        { id: 6, label: "Junio" },
        { id: 7, label: "Julio" },
        { id: 8, label: "Agosto" },
        { id: 9, label: "Septiembre" },
        { id: 10, label: "Octubre" },
        { id: 11, label: "Noviembre" },
        { id: 12, label: "Diciembre" },
      ],
      fields: [
        { key: "year", label: "Año" },
        { key: "month", label: "Mes" },
        { key: "start_date", label: "Fecha Inicio" },
        { key: "end_date", label: "Fecha Fin" },
        { key: "status", label: "Estado" },
        { key: "total_aprobadas", label: "Aprobadas" },
        { key: "total_pendientes", label: "Pendientes" },
        { key: "total_anuladas", label: "Anuladas" },
        { key: "monto_total_pagado", label: "Monto Pagado" },
        { key: "actions", label: "Acciones", thClass: "text-center", tdClass: "text-nowrap" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      currentUser: "auth/currentUser",
    }),
    openPeriod() {
      return this.periods.find((p) => p.status === 1) || null;
    },
  },
  created() {
    this.fetchPeriods();
  },
  methods: {
    async fetchPeriods() {
      try {
        this.loading = true;
        const { data } = await PayrollPeriodService.listPeriods();
        this.periods = data;
      } catch (error) {
        this.showToastError(
          error.response?.data || "Error al obtener los periodos de planilla."
        );
      } finally {
        this.loading = false;
      }
    },

    monthName(month) {
      const found = this.months.find((m) => m.id === Number(month));
      return found ? found.label : month;
    },

    formatCurrency(value) {
      return Number(value ?? 0).toFixed(2);
    },

    openOpenModal() {
      const latest = this.periods[0];
      let year = new Date().getFullYear();
      let month = new Date().getMonth() + 1;

      if (latest) {
        month = Number(latest.month) + 1;
        year = Number(latest.year);
        if (month > 12) {
          month = 1;
          year += 1;
        }
      }

      this.openForm = { year, month };
      this.showOpenModal = true;
    },

    async confirmOpenPeriod() {
      try {
        this.isBusy = true;
        this.addPreloader();
        await PayrollPeriodService.openPeriod({
          year: this.openForm.year,
          month: this.openForm.month,
          user_id: this.currentUser.user_id,
        });
        this.showToastSuccess(this.$t("Periodo abierto correctamente."));
        this.showOpenModal = false;
        await this.fetchPeriods();
      } catch (error) {
        this.showToastError(
          error.response?.data || "Error al abrir el periodo."
        );
      } finally {
        this.isBusy = false;
        this.removePreloader();
      }
    },

    openCloseSummary(period) {
      this.selectedPeriod = period;
      this.showCloseSummaryModal = true;
    },

    openExportAfpnetModal(period) {
      this.selectedPeriod = period;
      this.showExportAfpnetModal = true;
    },

    openPagoAfpModal(period) {
      this.selectedPeriod = period;
      this.showPagoAfpModal = true;
    },

    async downloadReportePagos(period) {
      try {
        this.addPreloader();
        const response = await PayrollPeriodService.exportReportePagos({
          period_id: period.id,
        });
        const filename = `reporte_pagos_planilla_${period.year}_${period.month}.xlsx`;
        this.forceFileDownload(response.data, filename);
        this.showToastSuccess(this.$t("Reporte de pagos descargado correctamente."));
      } catch (error) {
        let msg = "Error al descargar el reporte de pagos.";
        if (error.response?.data) {
          if (error.response.data instanceof Blob) {
            try {
              const text = await error.response.data.text();
              const parsed = JSON.parse(text);
              msg = parsed.message || parsed;
            } catch (e) {
              try {
                msg = await error.response.data.text();
              } catch (e2) {}
            }
          } else {
            msg = error.response.data.message || error.response.data;
          }
        }
        this.showToastError(msg);
      } finally {
        this.removePreloader();
      }
    },

    onPeriodClosed() {
      this.showCloseSummaryModal = false;
      this.fetchPeriods();
    },
  },
};
</script>
